import {
  Directive,
  Input,
  ViewContainerRef,
  ComponentRef,
  Component,
  HostListener,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  inject,
  Injector,
  Renderer2,
  ElementRef,
  HostBinding,
  OnDestroy,
} from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { SnackBarService } from '../../services/snack-bar.service';
import { SubSink } from 'SubSink';
import { ScreenService } from 'src/app/core/screen.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'copy-button',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  template: `
    <button
      mat-icon-button
      class="copy-button"
      type="button"
      [attr.aria-label]="ariaLabel"
      (click)="clicked.emit()"
      [ngClass]='size'
    >
      <mat-icon>{{ icon }}</mat-icon>
    </button>
  `,
  styleUrls: ['./copy.directive.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
class CopyButtonComponent {
  @Input() icon = 'content_copy';
  @Input() ariaLabel = 'Copy to clipboard';
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() class!: string | undefined;
  @Output() clicked = new EventEmitter<void>();

  @HostBinding('class') get hostClass() {
    return this.class;
  }
}

@Directive({
  selector: '[copy]',
  standalone: true,
})
export class CopyDirective implements OnDestroy {
  @Input('copy') value: any;
  @Input() copiedMessage?: string;
  @Input('copyButtonIcon') icon: string = 'content_copy';
  @Input('copyButtonSize') size: 'small' | 'medium' | 'large' = 'medium';
  @Input('copyButtonClass') class!: string | undefined;
  @Input('copyButtonAriaLabel') ariaLabel: string = 'Copy to clipboard';

  private clipboard = inject(Clipboard);
  private translate = inject(TranslateService);
  private snackbar = inject(SnackBarService);
  private injector = inject(Injector);
  private renderer = inject(Renderer2);
  private host = inject(ElementRef<HTMLElement>);
  private screenService = inject(ScreenService)

  private readonly subs = new SubSink();
  private buttonRef?: ComponentRef<CopyButtonComponent>;

  constructor(private vcr: ViewContainerRef) { }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  @HostListener('click')
  async onClick(): Promise<void> {
    if (!this.value || await firstValueFrom(this.screenService.isAboveMd$)) {
      return;
    }

    this.copyToClipboard();
  }

  @HostListener('mouseenter')
  async onMouseEnter(): Promise<void> {
    console.log(this.value);
    if (!this.value?.trim() || this.buttonRef || await firstValueFrom(this.screenService.isBelowMd$)) {
      return;
    }

    this.buttonRef = this.vcr.createComponent(CopyButtonComponent, {
      injector: this.injector
    });

    this.buttonRef.setInput('icon', this.icon);
    this.buttonRef.setInput('size', this.size);
    this.buttonRef.setInput('class', this.class);
    this.buttonRef.setInput('ariaLabel', this.ariaLabel);

    this.subs.sink = this.buttonRef.instance.clicked.subscribe(() => {
      this.copyToClipboard();
    });

    const buttonEl = this.buttonRef.location.nativeElement;
    this.renderer.appendChild(this.host.nativeElement, buttonEl);
  }

  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.buttonRef) {
      this.buttonRef.destroy();
      this.buttonRef = undefined;
    }
  }

  private copyToClipboard(): void {
    const text = this.stringify(this.value);
    if (!text) return;

    this.clipboard.copy(text);
    const message =
      this.copiedMessage || this.translate.instant('ClipboardCopiedMessage');
    this.snackbar.openSuccessSnackBar(message);
  }

  private stringify(value: any): string {
    if (value == null) {
      return '';
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return value.toString();
  }
}
